@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import org.example.project.dashboard.AppDialogs

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.CellColor
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularSplitScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.enginetabular.parseSemicolonCsv
import org.example.project.enginekartei.KarteiApi
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.GlobalManager
import org.example.project.service.InfrastrukturApi
import org.example.project.service.ProzessLoggerService
import org.example.project.service.YardApi
import org.example.project.service.YardLiveSync
import org.example.project.service.ZustandManager
import org.example.project.utils.All
import org.jetbrains.compose.web.css.Color
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.boxSizing
import org.jetbrains.compose.web.css.color
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.fontSize
import org.jetbrains.compose.web.css.fontWeight
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.minHeight
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.textAlign
import org.jetbrains.compose.web.css.width
import org.jetbrains.compose.web.dom.*
import kotlin.js.Date

private data class WaBestandLadestelleRow(
    val standort: All,
    val active: Boolean,
    val lkwKey: String,
    val lhmTyp: String,
    val waPlatz: String,
    val tor: String,
    val palettenWerte: Map<String, String>
)

@Composable
private fun WaBestandLadestellenPopup(
    tour: String,
    standorte: List<All>,
    vorhandeneStandorte: List<All>,
    lkw: List<All>,
    palettenDefs: List<All>,
    defaultLkwKey: String,
    lkwLabel: (All) -> String,
    lhmOptions: List<String>,
    defaultLhmTyp: String,
    lhmForExisting: (All) -> String,
    waPlaetzeForStandort: (All) -> List<String>,
    torForWaPlatz: (String, String, String) -> String,
    onClose: () -> Unit,
    onSave: (List<WaBestandLadestelleRow>) -> Unit
) {
    fun existingFor(standort: All): All? = vorhandeneStandorte.firstOrNull {
        it.slotStandortId == standort.id || it.standort == standort.standort || it.standort == standort.id
    }
    val headers = remember(palettenDefs) {
        listOf("Aktiv", "Standort", "Fahrer / LKW", "LHM-Typ", "WA-Platz", "Tor") +
            palettenDefs.map { it.bezeichnung }
    }
    val initialRows = remember(tour, standorte, vorhandeneStandorte, lkw, palettenDefs) {
        standorte.map { standort ->
            val existing = existingFor(standort)
            val selectedLkw = lkw.firstOrNull {
                    existing != null && (it.waStandortId == existing.id || it.slotStandortId == existing.id)
                }?.let(lkwLabel).orEmpty().ifBlank { defaultLkwKey }
            val waPlatz = existing?.platzcode.orEmpty()
            standort.id to (
                listOf(
                    if (existing != null) "\u2705" else "\u2B1C",
                    standort.standort.ifBlank { standort.id },
                    selectedLkw,
                    existing?.let(lhmForExisting).orEmpty().ifBlank { defaultLhmTyp },
                    waPlatz,
                    existing?.tor?.toString().orEmpty().ifBlank {
                        torForWaPlatz(standort.id, waPlatz, "")
                    }
                ) + palettenDefs.map { def -> existing?.palettenWerte?.get(def.bezeichnung).orEmpty() }
            )
        }
    }
    val viewportWidth = window.innerWidth.coerceAtLeast(720)
    val viewportHeight = window.innerHeight.coerceAtLeast(560)
    val requiredWidth = (900 + palettenDefs.size * 155)
        .coerceAtMost(viewportWidth - 24)
        .coerceAtLeast(900.coerceAtMost(viewportWidth - 24))
    val requiredHeight = (300 + standorte.size * 58)
        .coerceAtMost(viewportHeight - 24)
        .coerceAtLeast(560.coerceAtMost(viewportHeight - 24))

    PopupEngineCard(
        windowKey = "wa-bestand-ladestellen-$tour",
        widthPx = requiredWidth,
        heightPx = requiredHeight,
        minWidthPx = requiredWidth,
        minHeightPx = requiredHeight,
        initialX = 12.0,
        initialY = 12.0,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({ style { padding(20.px); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(14.px); height(100.percent); boxSizing("border-box") } }) {
            Div({ style { textAlign("center"); fontSize(18.px); fontWeight("700"); color(DashboardTheme.TEXT_LIGHT) } }) {
                Text("Bestand & Ladestellen - $tour")
            }
            Div({ style { property("flex", "1 1 auto"); minHeight(0.px) } }) {
                TabularScreen(
                    screenKey = "WarenausgangVorbereitung_BestandLadestellen_$tour",
                    title = null,
                    headers = headers,
                    data = initialRows,
                    editable = true,
                    includeMetadata = false,
                    useUserSettings = false,
                    toggleColumns = setOf("Aktiv"),
                    nonEditableCols = setOf("Standort", "Tor"),
                    dropdownOptions = mapOf(
                        "Fahrer / LKW" to lkw.map(lkwLabel).filter { it.isNotBlank() }.distinct(),
                        "LHM-Typ" to lhmOptions
                    ),
                    rowDropdownOptions = { rowId, _, colIndex ->
                        if (colIndex == headers.indexOf("WA-Platz")) {
                            standorte.firstOrNull { it.id == rowId }?.let(waPlaetzeForStandort).orEmpty()
                        } else null
                    },
                    onCellEditTransform = { rowId, colName, value, row ->
                        row.toMutableList().also { updated ->
                            while (updated.size < headers.size) updated.add("")
                            if (colName == "WA-Platz") {
                                updated[headers.indexOf("Tor")] = torForWaPlatz(rowId, value, updated[headers.indexOf("Tor")])
                            }
                        }
                    },
                    allowCreate = false,
                    allowDelete = false,
                    allowImportButton = false,
                    allowExportButton = false,
                    allowSettingsButton = false,
                    allowRefreshButton = false,
                    allowSearchButton = false,
                    showFilterRow = false,
                    showFillerRows = false,
                    saveButtonLabel = "Übernehmen",
                    onCommit = { committed ->
                        val byStandortId = standorte.associateBy { it.id }
                        onSave(committed.mapNotNull { (id, cols) ->
                            val standort = byStandortId[id] ?: return@mapNotNull null
                            val active = cols.getOrElse(0) { "" }.let {
                                it == "\u2705" || it.equals("true", true) || it == "1"
                            }
                            if (!active) return@mapNotNull null
                            WaBestandLadestelleRow(
                                standort = standort,
                                active = true,
                                lkwKey = cols.getOrElse(2) { defaultLkwKey },
                                lhmTyp = cols.getOrElse(3) { defaultLhmTyp },
                                waPlatz = cols.getOrElse(4) { "" },
                                tor = cols.getOrElse(5) { "" },
                                palettenWerte = palettenDefs.mapIndexed { index, def ->
                                    def.bezeichnung to cols.getOrElse(6 + index) { "" }
                                }.toMap()
                            )
                        })
                    }
                )
            }
        }
    }
}

@Composable
private fun WaVorbereitungTableSection(
    title: String,
    content: @Composable () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(6.px)
            width(100.percent)
            height(100.percent)
            minHeight(0.px)
            boxSizing("border-box")
        }
    }) {
        Div({
            style {
                padding(0.px, 4.px, 6.px, 4.px)
                margin(0.px)
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(14.px)
                fontWeight("700")
                property("border-bottom", "1px solid ${DashboardTheme.BORDER_LIGHT}")
                boxSizing("border-box")
            }
        }) {
            Text(title)
        }
        Div({
            style {
                width(100.percent)
                minHeight(0.px)
                property("flex", "1 1 auto")
            }
        }) {
            content()
        }
    }
}

@Composable
fun WarenausgangVorbereitungScreen() {
    val scope = rememberCoroutineScope()
    val yardLiveVersion = YardLiveSync.version()
    var eintraege by remember { mutableStateOf(emptyList<All>()) }
    var waLkwEintraege by remember { mutableStateOf(emptyList<All>()) }
    var palettenDefs by remember { mutableStateOf(emptyList<All>()) }
    var lhmTypen by remember { mutableStateOf(emptyList<All>()) }
    var firmenregister by remember { mutableStateOf(emptyList<All>()) }
    var waPlaetzeBasis by remember { mutableStateOf(emptyList<String>()) }
    var waPlaetzeByCode by remember { mutableStateOf<Map<String, All>>(emptyMap()) }
    var verfuegbareToreByStandort by remember { mutableStateOf<Map<String, Set<Int>>>(emptyMap()) }
    var torcodeByStandortTor by remember { mutableStateOf<Map<Pair<String, Int>, String>>(emptyMap()) }
    var standorteByTour by remember { mutableStateOf<Map<String, List<All>>>(emptyMap()) }
    var cusStandorte by remember { mutableStateOf(emptyList<All>()) }
    var selectedTournummer by remember { mutableStateOf<String?>(null) }
    var bestandLadestellenPopupTour by remember { mutableStateOf<String?>(null) }
    var searchedKopf by remember { mutableStateOf<List<All>?>(emptyList()) }
    var searchedStandorte by remember { mutableStateOf<List<All>?>(emptyList()) }
    var searchedLkw by remember { mutableStateOf<List<All>?>(emptyList()) }

    LaunchedEffect(Unit) {
        val waPlaetze = InfrastrukturApi.loadPlaetze()
            .filter { it.typ.startsWith("WA", ignoreCase = true) && it.platzcode.isNotBlank() }
        waPlaetzeBasis = waPlaetze.map { it.platzcode }.sorted()
        waPlaetzeByCode = waPlaetze.associateBy { it.platzcode }
        val alleTore = InfrastrukturApi.loadTore()
        verfuegbareToreByStandort = alleTore
            .groupBy { it.standort }
            .mapValues { (_, tore) -> tore.filter { it.gesperrt != true }.mapNotNull { it.tor }.toSet() }
        torcodeByStandortTor = alleTore
            .mapNotNull { tor ->
                val torNr = tor.tor ?: return@mapNotNull null
                val torcode = tor.torcode.orEmpty()
                if (tor.standort.isBlank() || torcode.isBlank()) return@mapNotNull null
                (tor.standort to torNr) to torcode
            }
            .toMap()
        palettenDefs = YardApi.loadPalettenDefinitionen()
            .filter { it.aktivWa }
            .sortedBy { it.id.toIntOrNull() ?: 0 }
        lhmTypen = InfrastrukturApi.loadLhmTypen()
            .sortedBy { it.id.toIntOrNull() ?: 0 }
        firmenregister = RegistrierungApi.loadCompanies()
            .sortedBy { it.firma.lowercase() }
        cusStandorte = KarteiApi.loadStandorte()
            .sortedBy { it.standort.ifBlank { it.id }.lowercase() }
    }

    LaunchedEffect(yardLiveVersion) {
        searchedKopf = emptyList()
        searchedStandorte = emptyList()
        searchedLkw = emptyList()
        eintraege = YardApi.loadWaListe().sortedByDescending { it.datum }
        waLkwEintraege = YardApi.loadWaLkwListe()

        val map = mutableMapOf<String, List<All>>()
        eintraege.map { it.lieferscheinnummer }.filter { it.isNotBlank() }.distinct().forEach { tour ->
            map[tour] = YardApi.loadWaStandorteByTour(tour)
        }
        standorteByTour = map

        if (selectedTournummer.isNullOrBlank()) {
            selectedTournummer = eintraege.firstOrNull()?.lieferscheinnummer
        }
    }

    val firmenNameByFirmenId = firmenregister.associate { (it.firmenId.ifBlank { it.id }) to it.firma }

    val standortLabel = { standort: All ->
        standort.standort.ifBlank { standort.id }
    }
    val standorteByLabel = cusStandorte.associateBy { standortLabel(it) }
    val standorteById = cusStandorte.associateBy { it.id }
    val standortDropdownOptions = cusStandorte
        .map { standortLabel(it) }
        .filter { it.isNotBlank() }
    fun standortOrt(standort: All?): String = standort?.ort.orEmpty()
    fun standortDataFor(value: String): All? =
        standorteByLabel[value] ?: standorteById[value]

    fun standortDisplayValue(value: String): String =
        standortDataFor(value)?.let { standortLabel(it) }.orEmpty().ifBlank { value }

    fun standortIdForLabel(label: String): String =
        standortDataFor(label)?.id.orEmpty().ifBlank { label }
    fun lhmTypLabel(lhmTyp: All): String =
        lhmTyp.bezeichnung.ifBlank { lhmTyp.kurzbezeichnung.ifBlank { lhmTyp.id } }
    val lhmTypByLabel = lhmTypen.associateBy { lhmTypLabel(it) }
    val lhmTypById = lhmTypen.associateBy { it.id }
    val lhmDropdownOptions = lhmTypen
        .map { lhmTypLabel(it) }
        .filter { it.isNotBlank() }
    val defaultLhmTyp = lhmTypen.singleOrNull()
    fun lhmTypFor(value: String): All? =
        lhmTypByLabel[value] ?: lhmTypById[value]
    fun lhmDisplayValue(value: String): String =
        lhmTypFor(value)?.let { lhmTypLabel(it) }.orEmpty().ifBlank { value }
    fun lhmDisplayFor(entry: All): String =
        entry.slotLhmTyp
            .ifBlank { entry.slotLhmTypId }
            .ifBlank { defaultLhmTyp?.let { lhmTypLabel(it) }.orEmpty() }
            .let(::lhmDisplayValue)
    fun checkboxText(value: Boolean): String = if (value) "\u2705" else "\u2B1C"
    fun checkboxValue(value: String): Boolean =
        value == "\u2705" || value.equals("true", true) || value.equals("ja", true) || value == "1"
    fun zustandAnzeige(zustand: Int): String =
        "$zustand - ${ZustandManager.textFor(zustand)}"
    fun zustandFromAnzeige(value: String): Int =
        value.substringBefore(" - ").trim().toIntOrNull()
            ?: ZustandManager.fromText(value)

    val palettenHeaders = palettenDefs.map { it.bezeichnung }
    val headersMaster = listOf("lfd. Nr.", "Datum", "lieferscheinnummer", "Empfaenger", "Auftraggeber", "Unternehmer") +
        palettenHeaders +
        listOf(
            "LHM-Typ", "Gesamt", "Ist verladen", "Offen", "Ladestellen", "Reihenfolge", "Kontrolliert", "Prio", "Verladedatum", "Verladeuhrzeit", "Zustand"
        )

    val belegteWaPlaetze = remember(standorteByTour) {
        standorteByTour.values
            .flatten()
            .filter { it.platzcode.isNotBlank() && it.zustand < 60 }
            .map { it.platzcode }
            .toSet()
    }
    val belegteWaPlaetzeAktuelleTour = remember(selectedTournummer, standorteByTour) {
        standorteByTour[selectedTournummer].orEmpty()
            .filter { it.platzcode.isNotBlank() && it.zustand < 60 }
            .map { it.platzcode }
            .toSet()
    }
    val waPlaetze = remember(waPlaetzeBasis, belegteWaPlaetze, belegteWaPlaetzeAktuelleTour) {
        waPlaetzeBasis.filter { platzcode ->
            platzcode !in belegteWaPlaetze || platzcode in belegteWaPlaetzeAktuelleTour
        }
    }

    val zustandIndex = headersMaster.indexOf("Zustand")
    val cellBackgroundColors = remember(eintraege, headersMaster) {
        buildMap {
            if (zustandIndex >= 0) {
                eintraege.forEach {
                    put(
                        it.lieferscheinnummer.ifBlank { it.lfdNr.toString() } to zustandIndex,
                        CellColor(Color(ZustandManager.colorHexFor(it.zustand)))
                    )
                }
            }
        }
    }

    fun palettenSummenByAuftrag(entry: All): Map<String, Int> {
        val standorte = standorteByTour[entry.lieferscheinnummer].orEmpty()
        val keys = (palettenDefs.map { it.bezeichnung } + standorte.flatMap { it.palettenWerte.keys } + entry.palettenWerte.keys)
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()
        val standortSummen = standorte
            .flatMap { standort ->
                keys.map { key -> key to (standort.palettenWerte[key]?.toIntOrNull() ?: 0) }
            }
            .groupBy({ it.first }, { it.second })
            .mapValues { (_, values) -> values.sum() }
        if (keys.isEmpty()) return mapOf(
            "Paletten" to (standorte.sumOf { it.palettenanzahl.coerceAtLeast(0) }.takeIf { it > 0 }
                ?: entry.palettenanzahl.coerceAtLeast(0))
        )
        return keys.associate { key ->
            key to (standortSummen[key]?.takeIf { it > 0 }
                ?: entry.palettenWerte[key]?.toIntOrNull()
                ?: 0)
        }
    }

    fun waVorbereitungIstAbgeschlossen(entry: All): Boolean =
        entry.zustand >= ZustandManager.ABGESCHLOSSEN && entry.zustand != ZustandManager.STORNIERT

    fun waVorbereitungIstPaletten(entry: All): Int =
        entry.yardabschlussIstPaletten.takeIf { it > 0 }
            ?: entry.mobilIstPaletten.takeIf { it > 0 }
            ?: entry.palettenanzahl.takeIf { entry.zustand >= ZustandManager.ABGESCHLOSSEN && it > 0 }
            ?: 0

    fun palettenGesamtByAuftrag(entry: All): Int =
        palettenSummenByAuftrag(entry).values.sum().takeIf { it > 0 }
            ?: entry.palettenanzahl.coerceAtLeast(0)

    fun istPalettenByAuftrag(entry: All): Int =
        entry.yardabschlussIstPaletten.takeIf { it > 0 }
            ?: waLkwEintraege
                .filter { it.lieferscheinnummer == entry.lieferscheinnummer && waVorbereitungIstAbgeschlossen(it) }
                .sumOf(::waVorbereitungIstPaletten)

    fun lhmTypByAuftrag(entry: All): String =
        entry.slotLhmTyp
            .ifBlank {
                waLkwEintraege
                    .firstOrNull { it.lieferscheinnummer == entry.lieferscheinnummer && it.slotLhmTyp.isNotBlank() }
                    ?.slotLhmTyp
                    .orEmpty()
            }

    fun masterPalettenGesamt(row: List<String>): Int =
        palettenDefs.indices.sumOf { index ->
            row.getOrElse(6 + index) { "" }.toIntOrNull() ?: 0
        }

    fun standortCount(tour: String): Int = standorteByTour[tour]?.size ?: 0
    fun standortOrderText(tour: String): String {
        val rows = standorteByTour[tour].orEmpty()
            .sortedBy { if (it.ablaufPrio > 0) it.ablaufPrio else Int.MAX_VALUE }
        if (rows.isEmpty()) return ""
        return rows.joinToString(" -> ") { it.standort.ifBlank { it.ort.ifBlank { it.id } } }
    }

    fun mapMasterRow(e: All): List<String> {
        val mandantName = firmenNameByFirmenId[e.firmenId]
            ?: e.firma
        val palettenSummen = palettenSummenByAuftrag(e)
        val palettenValues = palettenDefs.map { def ->
            palettenSummen[def.bezeichnung]?.takeIf { it > 0 }?.toString().orEmpty()
        }
        val gesamt = palettenGesamtByAuftrag(e)
        val ist = istPalettenByAuftrag(e)
        return listOf(
            e.lfdNr.toString(),
            e.datum,
            e.lieferscheinnummer,
            e.empfaenger,
            e.auftraggeber,
            e.unternehmer.ifBlank { mandantName }
        ) +
            palettenValues +
            listOf(
                lhmTypByAuftrag(e),
                gesamt.toString(),
                ist.takeIf { it > 0 }?.toString().orEmpty(),
                (gesamt - ist).coerceAtLeast(0)
                    .takeIf { gesamt > 0 }
                    ?.toString()
                    .orEmpty(),
                standortCount(e.lieferscheinnummer).toString(),
                standortOrderText(e.lieferscheinnummer),
                checkboxText(e.kontrolliert),
                e.prio,
                e.verladedatum,
                e.verladeuhrzeit,
                zustandAnzeige(e.zustand)
            )
    }

    fun buildWaMasterSave(rowKey: String, cols: List<String>, existing: All?): All {
        val base = 7 + palettenDefs.size
        val lieferscheinnummer = cols.getOrElse(2) { existing?.lieferscheinnummer.orEmpty() }.ifBlank { rowKey }
        return (existing ?: All()).copy(
            id = existing?.id.orEmpty().ifBlank { rowKey },
            lfdNr = existing?.lfdNr ?: 0,
            datum = cols.getOrElse(1) { existing?.datum.orEmpty() },
            lieferscheinnummer = lieferscheinnummer,
            empfaenger = cols.getOrElse(3) { existing?.empfaenger.orEmpty() },
            auftraggeber = cols.getOrElse(4) { existing?.auftraggeber.orEmpty() },
            unternehmer = cols.getOrElse(5) { existing?.unternehmer.orEmpty() },
            firmenId = existing?.firmenId.orEmpty(),
            firma = existing?.firma.orEmpty(),
            palettenWerte = existing?.palettenWerte.orEmpty(),
            platzcode = existing?.platzcode.orEmpty(),
            tor = existing?.tor,
            kontrolliert = existing?.kontrolliert == true,
            prio = cols.getOrElse(base + 6) { existing?.prio.orEmpty() },
            verladedatum = cols.getOrElse(base + 7) { existing?.verladedatum.orEmpty() },
            verladeuhrzeit = cols.getOrElse(base + 8) { existing?.verladeuhrzeit.orEmpty() },
            zustand = zustandFromAnzeige(
                cols.getOrElse(base + 9) {
                    zustandAnzeige(existing?.zustand ?: ZustandManager.ANGELEGT)
                }
            ),
            typ = cols.getOrElse(6 + palettenDefs.size) { existing?.typ.orEmpty() }
        )
    }

    val selectedStandorte = remember(selectedTournummer, standorteByTour) {
        standorteByTour[selectedTournummer].orEmpty()
            .sortedWith(
                compareBy<All> { if (it.ablaufPrio > 0) it.ablaufPrio else Int.MAX_VALUE }
                    .thenBy { it.id.toIntOrNull() ?: Int.MAX_VALUE }
            )
    }
    val selectedAuftrag = remember(selectedTournummer, eintraege) {
        eintraege.firstOrNull { it.lieferscheinnummer == selectedTournummer }
    }
    val selectedWaLkw = remember(selectedTournummer, waLkwEintraege) {
        waLkwEintraege.filter { it.lieferscheinnummer == selectedTournummer }
    }
    fun gebuchterWaLkw(): All? =
        selectedWaLkw.firstOrNull { it.slotStandortId.isNotBlank() && it.verladeuhrzeit.isNotBlank() }

    fun waStandortKopfQuelle(existing: All?): All? =
        existing?.takeIf {
            it.waSammelPin.isNotBlank() ||
                it.waSammelId.isNotBlank() ||
                it.slotLadenTouren.isNotBlank() ||
                it.lkwkennzeichen.isNotBlank()
        } ?: gebuchterWaLkw() ?: selectedAuftrag

    fun waLkwStandortId(lkw: All): String =
        lkw.waStandortId
            .ifBlank { lkw.slotStandortId }
            .ifBlank { lkw.slotAbholort }
            .ifBlank { lkw.standort.takeIf { candidate -> selectedStandorte.any { it.id == candidate } }.orEmpty() }

    fun gebuchterStandortId(): String =
        selectedWaLkw.firstOrNull { it.slotStandortId.isNotBlank() }
            ?.slotStandortId
            .orEmpty()
            .ifBlank { selectedAuftrag?.slotStandortId.orEmpty() }
            .ifBlank { selectedAuftrag?.standort?.takeIf { candidate -> selectedStandorte.any { it.id == candidate } }.orEmpty() }
            .ifBlank { selectedStandorte.firstOrNull { it.id == "1" }?.id.orEmpty() }
            .ifBlank { selectedStandorte.firstOrNull()?.id.orEmpty() }

    val detailHeaders = listOf(
        "ID", "Tour", "Kontrolliert", "Standort", "LHM-Typ", "Ort", "PLZ", "Stra\u00DFe", "Hausnummer", "WA-Platz", "Tor", "Standort-Prio"
    ) + palettenHeaders + listOf(
        "Soll Gesamt", "Ist verladen", "Offen", "Zustand"
    )
    val detailKontrolliertIndex = detailHeaders.indexOf("Kontrolliert")
    val detailStandortIndex = detailHeaders.indexOf("Standort")
    val detailLhmTypIndex = detailHeaders.indexOf("LHM-Typ")
    val detailOrtIndex = detailHeaders.indexOf("Ort")
    val detailPlzIndex = detailHeaders.indexOf("PLZ")
    val detailStrasseIndex = detailHeaders.indexOf("Stra\u00DFe")
    val detailHausnummerIndex = detailHeaders.indexOf("Hausnummer")
    val detailWaPlatzIndex = detailHeaders.indexOf("WA-Platz")
    val detailTorIndex = detailHeaders.indexOf("Tor")
    val detailPrioIndex = detailHeaders.indexOf("Standort-Prio")
    val detailPalettenStartIndex = palettenHeaders.firstOrNull()
        ?.let { detailHeaders.indexOf(it) }
        ?.takeIf { it >= 0 }
        ?: detailHeaders.indexOf("Soll Gesamt")
    val detailSollGesamtIndex = detailPalettenStartIndex + palettenDefs.size
    val detailIstPalettenIndex = detailSollGesamtIndex + 1
    val detailOffenIndex = detailIstPalettenIndex + 1
    val detailZustandIndex = detailHeaders.indexOf("Zustand")
    val detailCellBackgroundColors = remember(selectedStandorte, detailZustandIndex) {
        buildMap {
            if (detailZustandIndex >= 0) {
                selectedStandorte.forEach { standort ->
                    put(
                        standort.id to detailZustandIndex,
                        CellColor(Color(ZustandManager.colorHexFor(standort.zustand)))
                    )
                }
            }
        }
    }

    fun waVorbereitungSammelKey(entry: All?): String =
        entry?.waSammelPin.orEmpty().ifBlank { entry?.waSammelId.orEmpty() }.ifBlank { entry?.pin.orEmpty() }
            .trim()

    fun sammelKeyForTour(tour: String): String =
        waVorbereitungSammelKey(waLkwEintraege.firstOrNull { it.lieferscheinnummer == tour })
            .ifBlank { waVorbereitungSammelKey(eintraege.firstOrNull { it.lieferscheinnummer == tour }) }

    fun sammelTourenFor(tour: String): Set<String> {
        val key = sammelKeyForTour(tour)
        if (key.isBlank()) return setOf(tour).filter { it.isNotBlank() }.toSet()
        return waLkwEintraege
            .filter { waVorbereitungSammelKey(it) == key }
            .map { it.lieferscheinnummer }
            .filter { it.isNotBlank() }
            .toSet()
            .ifEmpty { setOf(tour).filter { it.isNotBlank() }.toSet() }
    }

    fun sammelTorForTour(tour: String, currentStandortId: String = ""): String {
        val key = sammelKeyForTour(tour)
        if (key.isBlank()) return ""
        val groupTours = sammelTourenFor(tour)
        val standortTor = groupTours
            .flatMap { groupTour -> standorteByTour[groupTour].orEmpty() }
            .firstOrNull { standort ->
                standort.tor != null &&
                    (standort.lieferscheinnummer != tour || currentStandortId.isBlank() || standort.id != currentStandortId)
            }
            ?.tor
            ?.toString()
            .orEmpty()
        if (standortTor.isNotBlank()) return standortTor
        return waLkwEintraege
            .firstOrNull { waVorbereitungSammelKey(it) == key && it.tor != null }
            ?.tor
            ?.toString()
            .orEmpty()
    }

    fun autoTorForWaPlatz(
        platzcode: String,
        fallbackTor: String = "",
        tour: String = selectedTournummer.orEmpty(),
        currentStandortId: String = ""
    ): String {
        sammelTorForTour(tour, currentStandortId).takeIf { it.isNotBlank() }?.let { return it }
        val platz = waPlaetzeByCode[platzcode]
        val platzStandort = platz?.standort.orEmpty()
        val verfuegbareTore = verfuegbareToreByStandort[platzStandort].orEmpty()
        val erlaubteTore = platz?.tore
            ?.filter { platzStandort !in verfuegbareToreByStandort || it in verfuegbareTore }
            ?.sorted()
            .orEmpty()
        return erlaubteTore.firstOrNull()?.toString() ?: fallbackTor
    }
    fun torcodeForWaPlatzTor(platzcode: String, tor: String): String {
        val platz = waPlaetzeByCode[platzcode]
        val torNr = tor.toIntOrNull() ?: return ""
        return torcodeByStandortTor[platz?.standort.orEmpty() to torNr].orEmpty()
    }
    fun waPlatzPasstZuStandort(platzcode: String, standortLabel: String): Boolean {
        if (platzcode.isBlank()) return true
        val platz = waPlaetzeByCode[platzcode] ?: return true
        val selectedStandortId = standortIdForLabel(standortLabel)
        return selectedStandortId.isBlank() || platz.standort == selectedStandortId
    }
    fun detailPalettenGesamt(row: List<String>): Int =
        palettenDefs.indices.sumOf { index ->
            row.getOrElse(detailPalettenStartIndex + index) { "" }.toIntOrNull() ?: 0
        }

    fun lkwForStandort(s: All): List<All> =
        selectedWaLkw.filter {
            val lkwStandortId = waLkwStandortId(it)
            (s.id.isNotBlank() && lkwStandortId.isNotBlank() && lkwStandortId == s.id) ||
                lkwStandortId.isBlank() &&
                (it.platzcode.isNotBlank() && it.platzcode == s.platzcode) ||
                lkwStandortId.isBlank() &&
                    (it.standort.isNotBlank() && it.standort == s.standort)
        }

    fun istPalettenForStandort(s: All): Int =
        lkwForStandort(s)
            .filter(::waVorbereitungIstAbgeschlossen)
            .sumOf(::waVorbereitungIstPaletten)

    fun mapDetailRow(s: All): List<String> {
        val palettenValues = palettenDefs.map { def ->
            s.palettenWerte[def.bezeichnung]?.toIntOrNull()?.takeIf { it > 0 }?.toString().orEmpty()
        }
        val soll = palettenValues.sumOf { it.toIntOrNull() ?: 0 }.takeIf { it > 0 }
            ?: s.palettenanzahl.coerceAtLeast(0)
        val ist = istPalettenForStandort(s)
        return listOf(
            s.id,
            s.lieferscheinnummer,
            checkboxText(s.kontrolliert),
            standortDisplayValue(s.standort),
            lhmDisplayFor(s),
            standortOrt(standortDataFor(s.standort)).ifBlank { s.ort },
            standortDataFor(s.standort)?.plz.orEmpty().ifBlank { s.plz },
            standortDataFor(s.standort)?.strasse.orEmpty().ifBlank { s.strasse },
            standortDataFor(s.standort)?.hausnummer.orEmpty().ifBlank { s.hausnummer }
        ) + listOf(
            s.platzcode,
            s.tor?.toString() ?: "",
            s.ablaufPrio.toString()
        ) + palettenValues + listOf(
            soll.takeIf { it > 0 }?.toString().orEmpty(),
            ist.takeIf { it > 0 }?.toString().orEmpty(),
            (soll - ist).coerceAtLeast(0).takeIf { soll > 0 }?.toString().orEmpty(),
            zustandAnzeige(s.zustand)
        )
    }

    val lkwHeaders = listOf(
        "Standort-ID", "PIN", "PIN bestätigt", "Fahrer", "LKW", "Auflieger", "WA-Platz", "Tor", "Ist verladen", "Leergut", "Status"
    )
    val lkwStandortIndex = lkwHeaders.indexOf("Standort-ID")
    val lkwPinIndex = lkwHeaders.indexOf("PIN")
    val lkwPinBestaetigtIndex = lkwHeaders.indexOf("PIN bestätigt")
    val lkwFahrerIndex = lkwHeaders.indexOf("Fahrer")
    val lkwLkwIndex = lkwHeaders.indexOf("LKW")
    val lkwAufliegerIndex = lkwHeaders.indexOf("Auflieger")
    val lkwWaPlatzIndex = lkwHeaders.indexOf("WA-Platz")
    val lkwTorIndex = lkwHeaders.indexOf("Tor")
    val lkwIstIndex = lkwHeaders.indexOf("Ist verladen")
    val lkwLeergutIndex = lkwHeaders.indexOf("Leergut")
    val lkwZustandIndex = lkwHeaders.indexOf("Status")
    val lkwCellBackgroundColors = remember(selectedWaLkw, lkwZustandIndex) {
        buildMap {
            if (lkwZustandIndex >= 0) {
                selectedWaLkw.forEach { lkw ->
                    put(lkw.id.ifBlank { lkw.lfdNr.toString() } to lkwZustandIndex, CellColor(Color(ZustandManager.colorHexFor(lkw.zustand))))
                }
            }
        }
    }

    fun waStandortSoll(standort: All?): Int =
        standort?.palettenanzahl?.takeIf { it > 0 }
            ?: standort?.palettenWerte.orEmpty().values.sumOf { it.toIntOrNull() ?: 0 }

    fun waLkwIstVerladen(lkw: All): Int =
        waVorbereitungIstPaletten(lkw).takeIf { it > 0 }
            ?: if (lkw.palettenWerte["waIstPalettenErfasst"].orEmpty().let { it.equals("true", true) || it == "1" }) lkw.palettenanzahl else 0

    fun mapLkwRow(lkw: All): List<String> =
        selectedStandorte.firstOrNull { it.id == waLkwStandortId(lkw) }.let { lkwStandort ->
        listOf(
            waLkwStandortId(lkw),
            yardPinCode(lkw),
            yardPinBestaetigtLabel(lkw),
            lkw.fahrername,
            listOf(
                lkw.lkwkennzeichen,
                lkw.fahrername.ifBlank { lkw.name }
            ).filter { it.isNotBlank() }.joinToString(" - "),
            lkw.aufliegerkennzeichen,
            lkwStandort?.platzcode.orEmpty().ifBlank { lkw.platzcode },
            lkwStandort?.tor?.toString().orEmpty().ifBlank { lkw.tor?.toString().orEmpty() },
            waLkwIstVerladen(lkw).takeIf { it > 0 }?.toString().orEmpty(),
            lkw.leerpalettenanzahl.takeIf { it > 0 }?.toString().orEmpty(),
            zustandAnzeige(lkw.zustand)
        )
        }

    fun waKontrollFeed(eventType: String, message: String, after: All, before: All? = null): All {
        val timestamp = Date().getTime().toLong()
        val date = Date(timestamp.toDouble())
        val feedUhrzeit = "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
        return All(
            typ = eventType,
            notiz = message,
            feedArt = eventType,
            feedEvent = message,
            feedEventLabel = message,
            feedLsNummer = after.lieferscheinnummer,
            feedUhrzeit = feedUhrzeit,
            firmenId = after.firmenId,
            firma = after.firma,
            lfdNr = after.lfdNr,
            lieferscheinnummer = after.lieferscheinnummer,
            lkwkennzeichen = after.lkwkennzeichen,
            auftragsart = "WA",
            palettenanzahl = after.palettenanzahl,
            fahrername = after.fahrername,
            zustand = after.zustand,
            datum = after.datum,
            uhrzeitAnmeldung = after.uhrzeitAnmeldung,
            timestamp = timestamp.toString(),
            status = ZustandManager.textFor(after.zustand)
        )
    }

    suspend fun refreshStandorteForTour(tour: String) {
        val fresh = YardApi.loadWaStandorteByTour(tour)
        standorteByTour = standorteByTour.toMutableMap().apply { put(tour, fresh) }
    }

    suspend fun syncWaKopfFromStandorte(tour: String, standorte: List<All>): All? {
        val auftrag = eintraege.firstOrNull { it.lieferscheinnummer == tour } ?: return null
        val palettenKeys = (palettenDefs.map { it.bezeichnung } + standorte.flatMap { it.palettenWerte.keys } + auftrag.palettenWerte.keys)
            .map { it.trim() }
            .filter { it.isNotBlank() }
            .distinct()
        val summed = palettenKeys.associateWith { key ->
            standorte.sumOf { it.palettenWerte[key]?.toIntOrNull() ?: 0 }
                .takeIf { it > 0 }
                ?.toString()
                .orEmpty()
        }
        val alleKontrolliert = standorte.isNotEmpty() && standorte.all { it.kontrolliert }
        val istPaletten = istPalettenByAuftrag(auftrag)
        val sollPaletten = summed.values.sumOf { it.toIntOrNull() ?: 0 }
            .takeIf { it > 0 }
            ?: standorte.sumOf { it.palettenanzahl.coerceAtLeast(0) }
        val offenerRest = (sollPaletten - istPaletten).coerceAtLeast(0)
        val hasOpenStandort = standorte.any {
            it.zustand < ZustandManager.ABGESCHLOSSEN &&
                ((it.palettenWerte.values.sumOf { value -> value.toIntOrNull() ?: 0 }) > 0 ||
                    it.palettenanzahl > 0)
        }
        val hasActiveLkw = waLkwEintraege.any {
            it.lieferscheinnummer == tour &&
                it.zustand >= ZustandManager.PIN_ABFRAGE &&
                it.zustand < ZustandManager.ABGESCHLOSSEN
        }
        val kopfZustand = when {
            sollPaletten > 0 && istPaletten == sollPaletten && !hasOpenStandort ->
                auftrag.zustand.takeIf { it >= ZustandManager.VERWALTUNGSABSCHLUSS } ?: ZustandManager.ABGESCHLOSSEN
            hasActiveLkw -> ZustandManager.IN_BEARBEITUNG
            istPaletten > 0 -> ZustandManager.TEILVERLADEN
            auftrag.zustand == ZustandManager.ABGESCHLOSSEN -> ZustandManager.ANGELEGT
            else -> auftrag.zustand.takeIf { it != ZustandManager.KONTROLLIERT } ?: ZustandManager.ANGELEGT
        }
        val updatedAuftrag = auftrag.copy(
            palettenWerte = summed,
            palettenanzahl = sollPaletten,
            kontrolliert = alleKontrolliert,
            zustand = kopfZustand,
            yardabschlussIstPaletten = istPaletten,
            yardabschlussOffenPaletten = offenerRest,
            slotLhmTypId = standorte.firstNotNullOfOrNull { it.slotLhmTypId.takeIf(String::isNotBlank) }.orEmpty(),
            slotLhmTyp = standorte.firstNotNullOfOrNull { it.slotLhmTyp.takeIf(String::isNotBlank) }.orEmpty(),
            lhmTypId = standorte.firstNotNullOfOrNull { it.lhmTypId.takeIf(String::isNotBlank) }.orEmpty(),
            typ = standorte.firstNotNullOfOrNull { it.typ.takeIf(String::isNotBlank) }.orEmpty()
        )
        YardApi.saveWaVorgang(updatedAuftrag)
        eintraege = eintraege.map {
            if (it.lieferscheinnummer == tour) updatedAuftrag else it
        }
        return updatedAuftrag
    }

    fun csvImportKey(value: String): String =
        value.trim()
            .removePrefix("\uFEFF")
            .lowercase()
            .replace("ä", "ae")
            .replace("ö", "oe")
            .replace("ü", "ue")
            .replace("ß", "ss")
            .replace(Regex("[^a-z0-9]+"), "")

    fun csvIndex(headers: List<String>, aliases: List<String>): Int =
        aliases.map(::csvImportKey)
            .firstNotNullOfOrNull { alias -> headers.indexOfFirst { csvImportKey(it) == alias }.takeIf { it >= 0 } }
            ?: -1

    fun csvValue(row: List<String>, index: Int): String =
        index.takeIf { it in row.indices }?.let { row[it].trim() }.orEmpty()

    fun csvMappedValue(row: List<String>, csvHeaders: List<String>, tableHeader: String): String {
        val target = csvImportKey(tableHeader)
        val index = csvHeaders.indexOfFirst { csvImportKey(it) == target }
        return csvValue(row, index)
    }

    fun waCsvPalettenWerte(row: List<String>, csvHeaders: List<String>): Map<String, String> {
        val byPalettenart = palettenDefs.associate { def ->
            val index = csvHeaders.indexOfFirst { csvHeader ->
                csvImportKey(csvHeader) == csvImportKey(def.bezeichnung) ||
                    csvImportKey(csvHeader) == csvImportKey(def.id) ||
                    csvImportKey(csvHeader) == csvImportKey(def.key)
            }
            def.bezeichnung to csvValue(row, index).filter { it.isDigit() }
        }
        if (byPalettenart.values.any { it.isNotBlank() }) return byPalettenart

        val genericIndex = csvIndex(csvHeaders, listOf("paletten", "anzahl", "menge", "palettenanzahl"))
        val genericValue = csvValue(row, genericIndex).filter { it.isDigit() }
        if (genericValue.isBlank()) return byPalettenart
        val defaultKey = palettenDefs.firstOrNull()?.bezeichnung ?: "Paletten"
        return byPalettenart + (defaultKey to genericValue)
    }

    suspend fun importWaVorbereitungCsv(csv: String) {
        val rows = parseSemicolonCsv(csv).filter { row -> row.any { it.isNotBlank() } }
        if (rows.size <= 1) {
            AppDialogs.alert("Leere CSV-Datei.")
            return
        }

        val csvHeaders = rows.first()
        val lsIndex = csvIndex(csvHeaders, listOf("lieferscheinnummer", "ls", "lsnummer", "lsnr", "lieferschein"))
        if (lsIndex < 0) {
            AppDialogs.alert("CSV-Spalte lieferscheinnummer fehlt.")
            return
        }

        val standortIdIndex = csvIndex(csvHeaders, listOf("standortid", "standortnr", "standortnummer", "id"))
        val standortIndex = csvIndex(csvHeaders, listOf("standort", "standortname", "ladestelle"))
        val affectedTours = mutableSetOf<String>()
        val standorteCache = standorteByTour.toMutableMap()
        val existingHeadsByTour = eintraege.associateBy { it.lieferscheinnummer }
        var importedRows = 0

        rows.drop(1).forEach { row ->
            val tour = csvValue(row, lsIndex)
            if (tour.isBlank()) return@forEach

            val existingHead = existingHeadsByTour[tour]
            val masterCols = MutableList(headersMaster.size) { "" }
            existingHead?.let { existing -> mapMasterRow(existing).forEachIndexed { index, value -> masterCols[index] = value } }
            headersMaster.forEachIndexed { index, header ->
                if (header in palettenHeaders) return@forEachIndexed
                val value = csvMappedValue(row, csvHeaders, header)
                if (value.isNotBlank()) masterCols[index] = value
            }
            masterCols[2] = masterCols.getOrElse(2) { "" }.ifBlank { tour }
            val headToSave = buildWaMasterSave(tour, masterCols, existingHead).copy(lieferscheinnummer = tour)
            YardApi.saveWaVorgang(headToSave)

            val standortLabel = csvValue(row, standortIndex)
            val csvStandortId = csvValue(row, standortIdIndex)
            val palettenWerte = waCsvPalettenWerte(row, csvHeaders)
            val palettenSumme = palettenWerte.values.sumOf { it.toIntOrNull() ?: 0 }
            if (standortLabel.isNotBlank() || csvStandortId.isNotBlank() || palettenSumme > 0) {
                val currentStandorte = standorteCache[tour].orEmpty()
                val existingStandort = currentStandorte.firstOrNull { it.id == csvStandortId && csvStandortId.isNotBlank() }
                    ?: currentStandorte.firstOrNull { it.standort == standortLabel && standortLabel.isNotBlank() }
                val resolvedId = csvStandortId.ifBlank {
                    existingStandort?.id.orEmpty().ifBlank {
                        ((currentStandorte.maxOfOrNull { it.id.toIntOrNull() ?: 0 } ?: 0) + 1).toString()
                    }
                }
                val standortData = standortDataFor(standortLabel)
                val toSave = (existingStandort ?: All()).copy(
                    id = resolvedId,
                    lieferscheinnummer = tour,
                    standort = standortLabel.ifBlank { existingStandort?.standort.orEmpty() },
                    ort = standortOrt(standortData).ifBlank { existingStandort?.ort.orEmpty() },
                    plz = standortData?.plz.orEmpty().ifBlank { existingStandort?.plz.orEmpty() },
                    strasse = standortData?.strasse.orEmpty().ifBlank { existingStandort?.strasse.orEmpty() },
                    hausnummer = standortData?.hausnummer.orEmpty().ifBlank { existingStandort?.hausnummer.orEmpty() },
                    palettenanzahl = palettenSumme.takeIf { it > 0 } ?: existingStandort?.palettenanzahl ?: 0,
                    palettenWerte = if (palettenSumme > 0) palettenWerte else existingStandort?.palettenWerte.orEmpty(),
                    zustand = existingStandort?.zustand?.takeIf { it != ZustandManager.KONTROLLIERT } ?: ZustandManager.ANGELEGT,
                    kontrolliert = existingStandort?.kontrolliert == true,
                    slotStandortId = standortData?.id.orEmpty().ifBlank { resolvedId }
                )
                YardApi.saveWaStandort(tour, toSave)
                standorteCache[tour] = (currentStandorte.filterNot { it.id == resolvedId } + toSave)
                    .sortedWith(compareBy<All> { if (it.ablaufPrio > 0) it.ablaufPrio else Int.MAX_VALUE }.thenBy { it.id.toIntOrNull() ?: Int.MAX_VALUE })
            }

            affectedTours += tour
            importedRows++
        }

        eintraege = YardApi.loadWaListe().sortedByDescending { it.datum }
        val freshStandorte = standorteByTour.toMutableMap()
        affectedTours.forEach { tour ->
            val fresh = YardApi.loadWaStandorteByTour(tour)
            freshStandorte[tour] = fresh
            syncWaKopfFromStandorte(tour, fresh)
        }
        standorteByTour = freshStandorte
        selectedTournummer = affectedTours.firstOrNull() ?: selectedTournummer
        AppDialogs.alert("$importedRows Zeilen importiert.")
    }

    fun lkwAuswahlOption(lkw: All): String =
        listOf(
            lkw.lkwkennzeichen,
            lkw.fahrername.ifBlank { lkw.name }
        ).filter { it.isNotBlank() }.joinToString(" - ")

    fun lkwAuswahlOptionId(value: String): String =
        value.substringBefore(" - ").trim()

    fun buildZusaetzlicherWaLkw(newId: String, source: All, standort: All?, tour: String): All {
        val standortId = standort?.id.orEmpty()
        return source.copy(
            id = newId,
            lfdNr = newId.toIntOrNull() ?: 0,
            lieferscheinnummer = tour,
            standort = standort?.standort.orEmpty().ifBlank { source.standort },
            ort = standort?.ort.orEmpty(),
            plz = standort?.plz.orEmpty(),
            strasse = standort?.strasse.orEmpty(),
            hausnummer = standort?.hausnummer.orEmpty(),
            platzcode = standort?.platzcode.orEmpty(),
            platz = standort?.platzcode.orEmpty(),
            tor = standort?.tor,
            torcode = standort?.torcode,
            ablaufPrio = standort?.ablaufPrio ?: source.ablaufPrio,
            palettenanzahl = waStandortSoll(standort),
            leerpalettenanzahl = 0,
            zustand = ZustandManager.ANGELEGT,
            uhrzeitAnmeldung = "",
            uhrzeitTorvergabe = "",
            torZugewiesenUhrzeit = "",
            uhrzeitAbgeschlossen = "",
            kontrolliert = false,
            waStandortId = standortId,
            slotStandortId = standortId
        )
    }

    TabularSplitScreen(
        initialRatio = 0.62f,
        screen1 = {
            WaVorbereitungTableSection("Warenausgangs Auftrag") {
                TabularScreen(
                screenKey = "WarenausgangVorbereitung",
                rollenKey = "WarenausgangVorbereitungScreen",
                title = "Warenausgang - Vorbereitung (Auftragskopf)",
                headers = headersMaster,
                data = (searchedKopf ?: eintraege).map { it.lieferscheinnummer.ifBlank { it.lfdNr.toString() } to mapMasterRow(it) },
                editable = true,
                includeMetadata = true,
                nonEditableCols = buildSet {
                    add("lfd. Nr.")
                    addAll(palettenHeaders)
                    add("LHM-Typ")
                    add("Gesamt")
                    add("Ist verladen")
                    add("Offen")
                    add("Ladestellen")
                    add("Reihenfolge")
                    add("Kontrolliert")
                    add("Zustand")
                },
                calendarColumns = setOf("Verladedatum"),
                cellBackgroundColors = cellBackgroundColors,
                allowDelete = false,
                onCsvImportRequest = { csv ->
                    scope.launch {
                        importWaVorbereitungCsv(csv)
                    }
                },
                onRowClick = { id ->
                    selectedTournummer = (searchedKopf ?: eintraege)
                        .firstOrNull { it.lieferscheinnummer == id || it.lfdNr.toString() == id }?.lieferscheinnummer
                    searchedStandorte = emptyList()
                    searchedLkw = emptyList()
                },
                onSearchWithFilters = { filters ->
                    scope.launch { searchedKopf = YardApi.searchWaAuskunft(filters, "kopf") }
                },
                extraNavButtons = listOf(
                    TabularToolbarButton(
                        label = "Bestand & Ladestellen",
                        icon = "images/add_circle.svg",
                        enabled = selectedAuftrag != null,
                        onClick = { selectedTournummer?.let { bestandLadestellenPopupTour = it } }
                    )
                ),
                onCellEditTransform = { _, colName, _, row ->
                    row.toMutableList().also { updated ->
                        while (updated.size < headersMaster.size) updated.add("")
                        if (colName in palettenHeaders) {
                            val gesamtIndex = 7 + palettenDefs.size
                            val istIndex = gesamtIndex + 1
                            val offenIndex = gesamtIndex + 2
                            val gesamt = masterPalettenGesamt(updated)
                            val ist = updated.getOrElse(istIndex) { "" }.toIntOrNull() ?: 0
                            updated[gesamtIndex] = gesamt.toString()
                            updated[offenIndex] = (gesamt - ist).coerceAtLeast(0).toString()
                        }
                    }
                },
                onCommit = { rows ->
                    scope.launch {
                        val existingById = eintraege.associateBy { it.lieferscheinnummer.ifBlank { it.lfdNr.toString() } }
                        val savedByKey = mutableMapOf<String, All>()
                        rows.forEach { (id, cols) ->
                            val existing = existingById[id]
                            val toSave = buildWaMasterSave(id, cols, existing)
                            if (existing != toSave) {
                                savedByKey[id] = YardApi.saveWaVorgang(toSave) ?: toSave
                            }
                        }

                        if (savedByKey.isNotEmpty()) {
                            eintraege = eintraege.map { entry ->
                                val key = entry.lieferscheinnummer.ifBlank { entry.lfdNr.toString() }
                                savedByKey[key] ?: entry
                            }.sortedByDescending { it.datum }
                        }

                        if (selectedTournummer.isNullOrBlank()) {
                            selectedTournummer = eintraege.firstOrNull()?.lieferscheinnummer
                        }
                    }
                }
                )
            }
        },
        screen2 = selectedTournummer?.let { tour ->
            {
                TabularSplitScreen(
                    initialRatio = 0.58f,
                    screen1 = {
                        WaVorbereitungTableSection("Warenausgangs Standorte") {
                TabularScreen(
                    screenKey = "WarenausgangVorbereitung_Details_Lhm_$tour",
                    rollenKey = "WarenausgangVorbereitungScreen",
                    title = "Warenausgang - Standorte ($tour)",
                    headers = detailHeaders,
                    data = (searchedStandorte ?: selectedStandorte).map { it.id to mapDetailRow(it) },
                    cellBackgroundColors = detailCellBackgroundColors,
                    editable = true,
                    includeMetadata = true,
                    toggleColumns = setOf("Kontrolliert"),
                    nonEditableCols = setOf(
                        "ID", "Tour", "Ort", "PLZ", "Stra\u00DFe", "Hausnummer", "Tor", "Soll Gesamt", "Ist verladen", "Offen", "Zustand"
                    ),
                    dropdownOptions = mapOf(
                        "Standort" to standortDropdownOptions,
                        "LHM-Typ" to lhmDropdownOptions,
                        "WA-Platz" to waPlaetze
                    ),
                    rowDropdownOptions = { _, row, colIndex ->
                        if (colIndex == detailWaPlatzIndex) {
                            val standortInRow = row.getOrElse(detailStandortIndex) { "" }
                            val standortId = standortIdForLabel(standortInRow)
                            val currentWaPlatz = row.getOrElse(detailWaPlatzIndex) { "" }
                            (listOf(currentWaPlatz) + waPlaetze.filter { platzcode ->
                                val platzStandort = waPlaetzeByCode[platzcode]?.standort.orEmpty()
                                standortId.isBlank() || platzStandort == standortId
                            }).filter { it.isNotBlank() }.distinct()
                        } else {
                            null
                        }
                    },
                    onSearchWithFilters = { filters ->
                        scope.launch {
                            searchedStandorte = YardApi.searchWaAuskunft(filters, "standorte", tour)
                        }
                    },
                    onCellEditTransform = { rowKey, colName, value, row ->
                        row.toMutableList().also { updated ->
                            while (updated.size < detailHeaders.size) updated.add("")
                            if (colName == "Standort") {
                                val standortData = standortDataFor(value)
                                updated[detailOrtIndex] = standortOrt(standortData)
                                updated[detailPlzIndex] = standortData?.plz.orEmpty()
                                updated[detailStrasseIndex] = standortData?.strasse.orEmpty()
                                updated[detailHausnummerIndex] = standortData?.hausnummer.orEmpty()
                                if (!waPlatzPasstZuStandort(updated.getOrElse(detailWaPlatzIndex) { "" }, value)) {
                                    updated[detailWaPlatzIndex] = ""
                                    updated[detailTorIndex] = ""
                                }
                            }
                            if (colName == "WA-Platz") {
                                val standortInRow = updated.getOrElse(detailStandortIndex) { "" }
                                if (waPlatzPasstZuStandort(value, standortInRow)) {
                                    updated[detailTorIndex] = autoTorForWaPlatz(
                                        platzcode = value,
                                        fallbackTor = updated.getOrElse(detailTorIndex) { "" },
                                        tour = updated.getOrElse(1) { selectedTournummer.orEmpty() },
                                        currentStandortId = rowKey
                                    )
                                } else {
                                    updated[detailWaPlatzIndex] = ""
                                    updated[detailTorIndex] = ""
                                }
                            }
                            if (colName in palettenHeaders) {
                                val soll = detailPalettenGesamt(updated)
                                val ist = updated.getOrElse(detailIstPalettenIndex) { "" }.toIntOrNull() ?: 0
                                updated[detailSollGesamtIndex] = soll.toString()
                                updated[detailOffenIndex] = (soll - ist).coerceAtLeast(0).toString()
                            }
                        }
                    },
                    createRow = { _ ->
                        val nextId = ((selectedStandorte.maxOfOrNull { it.id.toIntOrNull() ?: 0 } ?: 0) + 1).toString()
                        buildList {
                            add(nextId)
                            add(tour)
                            add(checkboxText(false))
                            add("")
                            add(defaultLhmTyp?.let { lhmTypLabel(it) }.orEmpty())
                            add("")
                            add("")
                            add("")
                            add("")
                            add("")
                            add("")
                            add(nextId)
                            palettenDefs.forEach { _ -> add("") }
                            add("")
                            add("")
                            add("")
                            add(zustandAnzeige(ZustandManager.ANGELEGT))
                        }
                    },
                    onCommit = { rows ->
                        scope.launch {
                            val existingById = selectedStandorte.associateBy { it.id }
                            val nextStandorte = mutableListOf<All>()
                            rows.forEach { (id, cols) ->
                                val existing = existingById[id]
                                if (existing != null && cols == mapDetailRow(existing)) {
                                    nextStandorte += existing
                                    return@forEach
                                }
                                val standortLabel = cols.getOrElse(detailStandortIndex) { "" }.ifBlank { existing?.standort.orEmpty() }
                                val standortData = standortDataFor(standortLabel)
                                val lhmTypLabel = cols.getOrElse(detailLhmTypIndex) { existing?.slotLhmTyp.orEmpty() }
                                    .ifBlank { defaultLhmTyp?.let { lhmTypLabel(it) }.orEmpty() }
                                val lhmTyp = lhmTypFor(lhmTypLabel)
                                val requestedWaPlatz = cols.getOrElse(detailWaPlatzIndex) { existing?.platzcode.orEmpty() }.ifBlank {
                                    existing?.platzcode.orEmpty()
                                }
                                val waPlatz = requestedWaPlatz.takeIf { waPlatzPasstZuStandort(it, standortLabel) }.orEmpty()
                                val standortPrio = cols.getOrElse(detailPrioIndex) { "0" }.toIntOrNull() ?: 0
                                val kontrolliert = checkboxValue(cols.getOrElse(detailKontrolliertIndex) { checkboxText(existing?.kontrolliert == true) })
                                val palettenWerte = palettenDefs.mapIndexed { index, def ->
                                    def.bezeichnung to cols.getOrElse(detailPalettenStartIndex + index) {
                                        existing?.palettenWerte?.get(def.bezeichnung).orEmpty()
                                    }
                                }.toMap()
                                val gebuchterLkw = gebuchterWaLkw()
                                val kopfQuelle = waStandortKopfQuelle(existing)
                                val zeitfensterDatum = existing?.verladedatum ?: gebuchterLkw?.verladedatum.orEmpty()
                                val zeitfensterVon = existing?.verladeuhrzeit ?: gebuchterLkw?.verladeuhrzeit.orEmpty()
                                val autoTor = autoTorForWaPlatz(
                                    platzcode = waPlatz,
                                    fallbackTor = cols.getOrElse(detailTorIndex) { existing?.tor?.toString().orEmpty() },
                                    tour = tour,
                                    currentStandortId = id
                                )
                                val toSave = All(
                                    id = id,
                                    lieferscheinnummer = tour,
                                    firmenId = existing?.firmenId.orEmpty().ifBlank { kopfQuelle?.firmenId.orEmpty() },
                                    firma = existing?.firma.orEmpty().ifBlank { kopfQuelle?.firma.orEmpty() },
                                    fahrername = existing?.fahrername.orEmpty().ifBlank { kopfQuelle?.fahrername.orEmpty() },
                                    lkwkennzeichen = existing?.lkwkennzeichen.orEmpty().ifBlank { kopfQuelle?.lkwkennzeichen.orEmpty() },
                                    aufliegerkennzeichen = existing?.aufliegerkennzeichen.orEmpty().ifBlank { kopfQuelle?.aufliegerkennzeichen.orEmpty() },
                                    lieferant = existing?.lieferant.orEmpty().ifBlank { kopfQuelle?.lieferant.orEmpty() },
                                    empfaenger = existing?.empfaenger.orEmpty().ifBlank { kopfQuelle?.empfaenger.orEmpty() },
                                    auftraggeber = existing?.auftraggeber.orEmpty().ifBlank { kopfQuelle?.auftraggeber.orEmpty() },
                                    unternehmer = existing?.unternehmer.orEmpty().ifBlank { kopfQuelle?.unternehmer.orEmpty() },
                                    lieferantFirmenId = existing?.lieferantFirmenId.orEmpty().ifBlank { kopfQuelle?.lieferantFirmenId.orEmpty() },
                                    empfaengerFirmenId = existing?.empfaengerFirmenId.orEmpty().ifBlank { kopfQuelle?.empfaengerFirmenId.orEmpty() },
                                    auftraggeberFirmenId = existing?.auftraggeberFirmenId.orEmpty().ifBlank { kopfQuelle?.auftraggeberFirmenId.orEmpty() },
                                    unternehmerFirmenId = existing?.unternehmerFirmenId.orEmpty().ifBlank { kopfQuelle?.unternehmerFirmenId.orEmpty() },
                                    standort = standortLabel,
                                    ort = standortOrt(standortData).ifBlank { existing?.ort.orEmpty() },
                                    plz = standortData?.plz.orEmpty().ifBlank { existing?.plz.orEmpty() },
                                    strasse = standortData?.strasse.orEmpty().ifBlank { existing?.strasse.orEmpty() },
                                    hausnummer = standortData?.hausnummer.orEmpty().ifBlank { existing?.hausnummer.orEmpty() },
                                    platzcode = waPlatz,
                                    tor = autoTor.toIntOrNull(),
                                    torcode = torcodeForWaPlatzTor(waPlatz, autoTor).ifBlank { existing?.torcode.orEmpty() },
                                    ablaufPrio = standortPrio,
                                    palettenanzahl = palettenWerte.values.sumOf { it.toIntOrNull() ?: 0 },
                                    palettenWerte = palettenWerte,
                                    kontrolliert = kontrolliert,
                                    uhrzeitAnmeldung = existing?.uhrzeitAnmeldung ?: "",
                                    verladedatum = zeitfensterDatum,
                                    verladeuhrzeit = zeitfensterVon,
                                    uhrzeitVon = zeitfensterVon,
                                    zustand = existing?.zustand?.takeIf { it != ZustandManager.KONTROLLIERT } ?: ZustandManager.ANGELEGT,
                                    slotStandortId = standortData?.id.orEmpty().ifBlank { standortIdForLabel(standortLabel) },
                                    slotIndex = existing?.slotIndex.orEmpty().ifBlank { kopfQuelle?.slotIndex.orEmpty() },
                                    slotDriverUserId = existing?.slotDriverUserId.orEmpty().ifBlank { kopfQuelle?.slotDriverUserId.orEmpty() },
                                    slotYardStandortKey = existing?.slotYardStandortKey.orEmpty().ifBlank { kopfQuelle?.slotYardStandortKey.orEmpty() },
                                    slotLadenTouren = existing?.slotLadenTouren.orEmpty().ifBlank { kopfQuelle?.slotLadenTouren.orEmpty() },
                                    slotLhmTypId = lhmTyp?.id.orEmpty(),
                                    slotLhmTyp = lhmDisplayValue(lhmTypLabel),
                                    lhmTypId = lhmTyp?.id.orEmpty(),
                                    typ = lhmDisplayValue(lhmTypLabel),
                                    slotDatum = zeitfensterDatum,
                                    datum = zeitfensterDatum,
                                    waSammelPin = existing?.waSammelPin.orEmpty().ifBlank { kopfQuelle?.waSammelPin.orEmpty() },
                                    waSammelId = existing?.waSammelId.orEmpty().ifBlank { kopfQuelle?.waSammelId.orEmpty() },
                                    endToEndFlow = existing?.endToEndFlow == true || kopfQuelle?.endToEndFlow == true,
                                    flowTyp = existing?.flowTyp.orEmpty().ifBlank { kopfQuelle?.flowTyp.orEmpty() },
                                    flowStatus = existing?.flowStatus.orEmpty().ifBlank { kopfQuelle?.flowStatus.orEmpty() },
                                    flowId = existing?.flowId.orEmpty().ifBlank { kopfQuelle?.flowId.orEmpty() },
                                    flowTargetFirmenId = existing?.flowTargetFirmenId.orEmpty().ifBlank { kopfQuelle?.flowTargetFirmenId.orEmpty() },
                                    flowTargetWeId = existing?.flowTargetWeId.orEmpty().ifBlank { kopfQuelle?.flowTargetWeId.orEmpty() },
                                    flowTargetWeLieferscheinnummer = existing?.flowTargetWeLieferscheinnummer.orEmpty()
                                        .ifBlank { kopfQuelle?.flowTargetWeLieferscheinnummer.orEmpty() }
                                )

                                nextStandorte += toSave
                                if (existing != toSave) {
                                    YardApi.saveWaStandort(tour, toSave)

                                    if (existing != null && existing.ablaufPrio != toSave.ablaufPrio) {
                                        ProzessLoggerService.append(
                                            eventType = "STANDORT_PRIO",
                                            message = "Standortprioritat geandert (${toSave.standort.ifBlank { toSave.ort }} -> ${toSave.ablaufPrio})",
                                            after = toSave,
                                            before = existing
                                        )
                                    }
                                }
                            }
                            standorteByTour = standorteByTour.toMutableMap().apply { put(tour, nextStandorte) }
                            syncWaKopfFromStandorte(tour, nextStandorte)
                        }
                    },
                    onApproveSelected = { ids ->
                        scope.launch {
                            if (ids.isNotEmpty()) {
                                val existingById = selectedStandorte.associateBy { it.id }
                                ids.forEach { id ->
                                    existingById[id]?.let { before ->
                                        val updated = before.copy(
                                            kontrolliert = true,
                                            zustand = before.zustand.takeIf { it != ZustandManager.KONTROLLIERT } ?: ZustandManager.ANGELEGT
                                        )
                                        YardApi.saveWaStandort(tour, updated)
                                    }
                                }
                            }
                            val fresh = YardApi.loadWaStandorteByTour(tour)
                            standorteByTour = standorteByTour.toMutableMap().apply { put(tour, fresh) }
                            waLkwEintraege = YardApi.loadWaLkwListe()
                            val beforeKopf = eintraege.firstOrNull { it.lieferscheinnummer == tour }
                            val updatedKopf = syncWaKopfFromStandorte(tour, fresh)
                            if (beforeKopf != null && updatedKopf != null && !beforeKopf.kontrolliert && updatedKopf.kontrolliert) {
                                YardApi.appendWaVorgangslog(
                                    tour,
                                    waKontrollFeed(
                                        eventType = "AUFTRAG_KONTROLLIERT",
                                        message = "LS: $tour komplett kontrolliert",
                                        after = updatedKopf,
                                        before = beforeKopf
                                    ),
                                    updatedKopf.firmenId
                                )
                            }
                        }
                    },
                    onDeleteRows = { ids ->
                        scope.launch {
                            ids.forEach { id ->
                                YardApi.deleteWaStandort(tour, id)
                            }
                            val fresh = YardApi.loadWaStandorteByTour(tour)
                            standorteByTour = standorteByTour.toMutableMap().apply { put(tour, fresh) }
                            waLkwEintraege = YardApi.loadWaLkwListe()
                            syncWaKopfFromStandorte(tour, fresh)
                        }
                    }
                )
                        }
                    },
                    screen2 = {
                        WaVorbereitungTableSection("Warenausgangs LKWs") {
                        TabularScreen(
                            screenKey = "WarenausgangVorbereitung_Lkw_$tour",
                            rollenKey = "WarenausgangVorbereitungScreen",
                            title = "Warenausgang - LKWs ($tour)",
                            headers = lkwHeaders,
                            data = (searchedLkw ?: selectedWaLkw).map { it.id.ifBlank { it.lfdNr.toString() } to mapLkwRow(it) },
                            onSearchWithFilters = { filters ->
                                scope.launch {
                                    searchedLkw = YardApi.searchWaAuskunft(filters, "detail", tour)
                                }
                            },
                            onCommit = { rows ->
                                scope.launch {
                                    val existingById = selectedWaLkw.associateBy { it.id.ifBlank { it.lfdNr.toString() } }
                                    val lkwQuellen = selectedWaLkw
                                        .filter { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
                                    rows.forEach { (id, cols) ->
                                        if (id in existingById) return@forEach
                                        val sourceValue = cols.getOrElse(lkwLkwIndex) { "" }
                                        val source = lkwQuellen.firstOrNull { lkwAuswahlOption(it) == sourceValue }
                                            ?: selectedWaLkw.firstOrNull {
                                                it.id == lkwAuswahlOptionId(sourceValue) ||
                                                    it.lfdNr.toString() == lkwAuswahlOptionId(sourceValue) ||
                                                    it.lkwkennzeichen == sourceValue
                                            }
                                            ?: return@forEach
                                        val standortId = cols.getOrElse(lkwStandortIndex) { "" }.trim()
                                        val standort = selectedStandorte.firstOrNull { it.id == standortId } ?: return@forEach
                                        YardApi.saveWaLkwVorgang(buildZusaetzlicherWaLkw(id, source, standort, tour))
                                    }
                                    waLkwEintraege = YardApi.loadWaLkwListe()
                                    refreshStandorteForTour(tour)
                                }
                            },
                            onDeleteRows = { ids ->
                                ids.forEach { id ->
                                    check(YardApi.deleteWaLkwVorgang(tour, id)) { "WA-LKW $id konnte nicht gelöscht werden." }
                                }
                                waLkwEintraege = YardApi.loadWaLkwListe()
                                val fresh = YardApi.loadWaStandorteByTour(tour)
                                standorteByTour = standorteByTour.toMutableMap().apply { put(tour, fresh) }
                                val updatedKopf = syncWaKopfFromStandorte(tour, fresh)
                                if (updatedKopf != null) {
                                    eintraege = eintraege.map { if (it.lieferscheinnummer == tour) updatedKopf else it }
                                }
                            },
                            cellBackgroundColors = lkwCellBackgroundColors,
                            editable = true,
                            includeMetadata = true,
                            nonEditableCols = lkwHeaders.filterNot { it == "Standort-ID" || it == "LKW" }.toSet(),
                            dropdownOptions = mapOf(
                                "Standort-ID" to selectedStandorte
                                    .filter { it.zustand != ZustandManager.STORNIERT }
                                    .map { it.id },
                                "LKW" to selectedWaLkw
                                    .filter { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }
                                    .map { lkwAuswahlOption(it) }
                                    .distinct()
                            ),
                            onCellEditTransform = { _, colName, value, row ->
                                row.toMutableList().also { updated ->
                                    while (updated.size < lkwHeaders.size) updated.add("")
                                    val source = if (colName == "LKW") {
                                        selectedWaLkw.firstOrNull { lkwAuswahlOption(it) == value }
                                    } else {
                                        val selectedLkw = updated.getOrElse(lkwLkwIndex) { "" }
                                        selectedWaLkw.firstOrNull {
                                            lkwAuswahlOption(it) == selectedLkw ||
                                                it.lkwkennzeichen == selectedLkw
                                        }
                                    }
                                    val standort = if (colName == "Standort-ID") {
                                        selectedStandorte.firstOrNull { it.id == value }
                                    } else {
                                        selectedStandorte.firstOrNull { it.id == updated.getOrElse(lkwStandortIndex) { "" } }
                                    }
                                    if (source != null) {
                                        updated[lkwPinIndex] = yardPinCode(source)
                                        updated[lkwPinBestaetigtIndex] = yardPinBestaetigtLabel(source)
                                        updated[lkwFahrerIndex] = source.fahrername
                                        updated[lkwLkwIndex] = lkwAuswahlOption(source)
                                        updated[lkwAufliegerIndex] = source.aufliegerkennzeichen
                                        updated[lkwLeergutIndex] = source.leerpalettenanzahl.takeIf { it > 0 }?.toString().orEmpty()
                                    }
                                    if (standort != null) {
                                        updated[lkwWaPlatzIndex] = standort.platzcode
                                        updated[lkwTorIndex] = standort.tor?.toString().orEmpty()
                                        updated[lkwIstIndex] = source?.let { waLkwIstVerladen(it).takeIf { value -> value > 0 }?.toString() }.orEmpty()
                                    }
                                    updated[lkwZustandIndex] = zustandAnzeige(ZustandManager.ANGELEGT)
                                }
                            },
                            createRow = { newId ->
                                val standortId = gebuchterStandortId()
                                val standort = selectedStandorte.firstOrNull { it.id == standortId }
                                MutableList(lkwHeaders.size) { index ->
                                    when (index) {
                                        lkwStandortIndex -> standortId
                                        lkwLkwIndex -> ""
                                        lkwWaPlatzIndex -> standort?.platzcode.orEmpty()
                                        lkwTorIndex -> standort?.tor?.toString().orEmpty()
                                        lkwIstIndex -> ""
                                        lkwZustandIndex -> zustandAnzeige(ZustandManager.ANGELEGT)
                                        else -> ""
                                    }
                                }
                            },
                            allowDelete = true
                        )
                        }
                    }
                )
            }
        }
    )

    bestandLadestellenPopupTour?.let { popupTour ->
        val popupAuftrag = eintraege.firstOrNull { it.lieferscheinnummer == popupTour }
        val popupStandorte = standorteByTour[popupTour].orEmpty()
        val popupLkw = waLkwEintraege.filter { it.lieferscheinnummer == popupTour }
        val defaultLkw = popupLkw.firstOrNull {
            it.slotStandortId.isNotBlank() && it.verladeuhrzeit.isNotBlank()
        } ?: popupLkw.firstOrNull { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT }

        WaBestandLadestellenPopup(
            tour = popupTour,
            standorte = cusStandorte,
            vorhandeneStandorte = popupStandorte,
            lkw = popupLkw.filter { it.zustand < ZustandManager.ABGESCHLOSSEN && it.zustand != ZustandManager.STORNIERT },
            palettenDefs = palettenDefs,
            defaultLkwKey = defaultLkw?.let(::lkwAuswahlOption).orEmpty(),
            lkwLabel = ::lkwAuswahlOption,
            lhmOptions = lhmDropdownOptions,
            defaultLhmTyp = defaultLhmTyp?.let(::lhmTypLabel).orEmpty(),
            lhmForExisting = ::lhmDisplayFor,
            waPlaetzeForStandort = { standort ->
                waPlaetze.filter { platzcode ->
                    waPlaetzeByCode[platzcode]?.standort.orEmpty() == standort.id
                }
            },
            torForWaPlatz = { standortId, waPlatz, fallback ->
                autoTorForWaPlatz(
                    platzcode = waPlatz,
                    fallbackTor = fallback,
                    tour = popupTour,
                    currentStandortId = standortId
                )
            },
            onClose = { bestandLadestellenPopupTour = null },
            onSave = { activeRows ->
                scope.launch {
                    val currentStandorte = YardApi.loadWaStandorteByTour(popupTour)
                    val currentLkw = YardApi.loadWaLkwListe().filter { it.lieferscheinnummer == popupTour }
                    var nextStandortId = (currentStandorte.maxOfOrNull { it.id.toIntOrNull() ?: 0 } ?: 0) + 1
                    var nextLkwId = (currentLkw.maxOfOrNull { it.id.toIntOrNull() ?: it.lfdNr } ?: 0) + 1

                    activeRows.forEach { quick ->
                        val stammdaten = quick.standort
                        val existing = currentStandorte.firstOrNull {
                            it.slotStandortId == stammdaten.id || it.standort == stammdaten.standort || it.standort == stammdaten.id
                        }
                        val id = existing?.id.orEmpty().ifBlank { (nextStandortId++).toString() }
                        val source = currentLkw.firstOrNull { lkwAuswahlOption(it) == quick.lkwKey }
                            ?: defaultLkw
                            ?: popupAuftrag
                            ?: return@forEach
                        val lhm = lhmTypFor(quick.lhmTyp)
                            ?: lhmTypFor(existing?.slotLhmTyp.orEmpty().ifBlank { existing?.slotLhmTypId.orEmpty() })
                            ?: defaultLhmTyp
                        val palettenWerte = palettenDefs.associate { def ->
                            def.bezeichnung to quick.palettenWerte[def.bezeichnung].orEmpty()
                        }
                        val waPlatz = quick.waPlatz.takeIf {
                            waPlatzPasstZuStandort(it, standortLabel(stammdaten))
                        }.orEmpty()
                        val tor = quick.tor.ifBlank {
                            autoTorForWaPlatz(
                                platzcode = waPlatz,
                                fallbackTor = existing?.tor?.toString().orEmpty(),
                                tour = popupTour,
                                currentStandortId = id
                            )
                        }
                        val toSave = (existing ?: All()).copy(
                            id = id,
                            lieferscheinnummer = popupTour,
                            firmenId = existing?.firmenId.orEmpty().ifBlank { source.firmenId },
                            firma = existing?.firma.orEmpty().ifBlank { source.firma },
                            fahrername = source.fahrername,
                            lkwkennzeichen = source.lkwkennzeichen,
                            aufliegerkennzeichen = source.aufliegerkennzeichen,
                            lieferant = source.lieferant,
                            empfaenger = source.empfaenger,
                            auftraggeber = source.auftraggeber,
                            unternehmer = source.unternehmer,
                            lieferantFirmenId = source.lieferantFirmenId,
                            empfaengerFirmenId = source.empfaengerFirmenId,
                            auftraggeberFirmenId = source.auftraggeberFirmenId,
                            unternehmerFirmenId = source.unternehmerFirmenId,
                            standort = standortLabel(stammdaten),
                            ort = standortOrt(stammdaten),
                            plz = stammdaten.plz,
                            strasse = stammdaten.strasse,
                            hausnummer = stammdaten.hausnummer,
                            platzcode = waPlatz,
                            tor = tor.toIntOrNull(),
                            torcode = torcodeForWaPlatzTor(waPlatz, tor).ifBlank { existing?.torcode.orEmpty() },
                            ablaufPrio = existing?.ablaufPrio?.takeIf { it > 0 } ?: id.toIntOrNull() ?: 0,
                            palettenanzahl = palettenWerte.values.sumOf { it.toIntOrNull() ?: 0 },
                            palettenWerte = palettenWerte,
                            kontrolliert = existing?.kontrolliert == true,
                            zustand = existing?.zustand ?: ZustandManager.ANGELEGT,
                            verladedatum = existing?.verladedatum.orEmpty().ifBlank { source.verladedatum },
                            verladeuhrzeit = existing?.verladeuhrzeit.orEmpty().ifBlank { source.verladeuhrzeit },
                            uhrzeitVon = existing?.uhrzeitVon.orEmpty().ifBlank { source.verladeuhrzeit },
                            slotStandortId = stammdaten.id,
                            slotIndex = existing?.slotIndex.orEmpty().ifBlank { source.slotIndex },
                            slotDriverUserId = existing?.slotDriverUserId.orEmpty().ifBlank { source.slotDriverUserId },
                            slotYardStandortKey = existing?.slotYardStandortKey.orEmpty().ifBlank { source.slotYardStandortKey },
                            slotLadenTouren = existing?.slotLadenTouren.orEmpty().ifBlank { source.slotLadenTouren },
                            slotLhmTypId = lhm?.id.orEmpty(),
                            slotLhmTyp = lhm?.let(::lhmTypLabel).orEmpty(),
                            lhmTypId = lhm?.id.orEmpty(),
                            typ = lhm?.let(::lhmTypLabel).orEmpty(),
                            datum = existing?.datum.orEmpty().ifBlank { source.datum },
                            slotDatum = existing?.slotDatum.orEmpty().ifBlank { source.slotDatum.ifBlank { source.datum } },
                            waSammelPin = existing?.waSammelPin.orEmpty().ifBlank { source.waSammelPin },
                            waSammelId = existing?.waSammelId.orEmpty().ifBlank { source.waSammelId },
                            endToEndFlow = existing?.endToEndFlow == true || source.endToEndFlow,
                            flowTyp = existing?.flowTyp.orEmpty().ifBlank { source.flowTyp },
                            flowStatus = existing?.flowStatus.orEmpty().ifBlank { source.flowStatus },
                            flowId = existing?.flowId.orEmpty().ifBlank { source.flowId },
                            flowTargetFirmenId = existing?.flowTargetFirmenId.orEmpty().ifBlank { source.flowTargetFirmenId },
                            flowTargetWeId = existing?.flowTargetWeId.orEmpty().ifBlank { source.flowTargetWeId },
                            flowTargetWeLieferscheinnummer = existing?.flowTargetWeLieferscheinnummer.orEmpty()
                                .ifBlank { source.flowTargetWeLieferscheinnummer }
                        )
                        YardApi.saveWaStandort(popupTour, toSave)

                        val assigned = currentLkw.firstOrNull {
                            (it.waStandortId == id || it.slotStandortId == id) && lkwAuswahlOption(it) == quick.lkwKey
                        }
                        if (assigned == null) {
                            YardApi.saveWaLkwVorgang(buildZusaetzlicherWaLkw((nextLkwId++).toString(), source, toSave, popupTour))
                        }
                    }

                    val freshStandorte = YardApi.loadWaStandorteByTour(popupTour)
                    standorteByTour = standorteByTour.toMutableMap().apply { put(popupTour, freshStandorte) }
                    waLkwEintraege = YardApi.loadWaLkwListe()
                    val updatedKopf = syncWaKopfFromStandorte(popupTour, freshStandorte)
                    if (updatedKopf != null) {
                        eintraege = eintraege.map { if (it.lieferscheinnummer == popupTour) updatedKopf else it }
                    }
                    bestandLadestellenPopupTour = null
                }
            }
        )
    }
}

